import { FC, useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Space, Checkbox, message, Spin, Radio } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useUsers } from '~/services/user';
import { Role } from '~/utils/constants';
import { notificationsAPI } from '~/services/notifications/notifications.api';
import { useEmailConfirmation } from '~/hooks/useEmailConfirmation';

const { TextArea } = Input;
const { Option } = Select;

interface ManualMessageModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface RecipientOption {
  label: string;
  value: string;
  role: Role;
  email?: string;
}

export const ManualMessageModal: FC<ManualMessageModalProps> = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [recipientSelection, setRecipientSelection] = useState<'all' | 'some' | 'one'>('some');
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([Role.STAFF, Role.ADMIN]);
  const [sendMode, setSendMode] = useState<'individual' | 'group'>('individual');
  const { showConfirmation, ConfirmationModal } = useEmailConfirmation();

  // Fetch all users (staff and admins)
  const { data: usersData, isLoading: loadingUsers } = useUsers({
    roles: [Role.STAFF, Role.ADMIN, Role.SUPER_ADMIN],
    pageSize: 100,
  });

  const users = usersData?.items || [];

  // Generate recipient options
  const recipientOptions: RecipientOption[] = users.map((user) => ({
    label: `${user.firstName} ${user.lastName} (@${user.username}) - ${user.role}`,
    value: user.id,
    role: user.role,
    email: user.email,
  }));

  // Filter options based on selected roles
  const filteredOptions = recipientOptions.filter((opt) => selectedRoles.includes(opt.role));

  const handleSend = async () => {
    try {
      const values = await form.validateFields();

      let recipientIds: string[] = [];
      let selectedUsers: any[] = [];

      if (recipientSelection === 'all') {
        // Send to everyone (all staff and all admins)
        selectedUsers = users;
        recipientIds = users.map((u) => u.id);
      } else if (recipientSelection === 'some') {
        // Send to users matching selected roles
        selectedUsers = users.filter((u) => selectedRoles.includes(u.role as Role));
        recipientIds = selectedUsers.map((u) => u.id);
      } else {
        // Send to specifically selected users
        recipientIds = values.recipients;
        selectedUsers = users.filter((u) => recipientIds.includes(u.id));
      }

      if (recipientIds.length === 0) {
        message.error('Please select at least one recipient');
        return;
      }

      // Prepare recipients for confirmation modal
      const recipients = selectedUsers.map((user) => ({
        email: user.email || '',
        name: `${user.firstName} ${user.lastName}`,
        role: user.role,
      }));

      // Build confirmation description based on send mode
      const sendModeText = sendMode === 'individual'
        ? 'individually to each recipient'
        : 'to all recipients in one email';
      const actionDescription = `Sending manual message "${values.subject}" ${sendModeText}`;

      // Show confirmation modal
      showConfirmation(recipients, {
        actionDescription,
        onProceed: async (sendEmails: boolean) => {
          if (!sendEmails) {
            message.info('Message cancelled');
            return;
          }

          try {
            setSending(true);

            // Call API to send manual message
            await notificationsAPI.sendManualMessage({
              subject: values.subject,
              body: values.body,
              recipientIds,
              sendMode,
            });

            message.success(`Message sent to ${recipientIds.length} recipient(s)`);
            form.resetFields();
            onSuccess();
          } catch (error: any) {
            console.error('Failed to send message:', error);
            message.error(error?.response?.data?.message || 'Failed to send message');
          } finally {
            setSending(false);
          }
        },
      });
    } catch (error: any) {
      console.error('Validation failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setRecipientSelection('some');
    setSelectedRoles([Role.STAFF, Role.ADMIN]);
    setSendMode('individual');
    onClose();
  };

  return (
    <>
      <ConfirmationModal />
      <Modal
        title="Send Manual Message"
        open={open}
        onCancel={handleClose}
        width={700}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={sending}
          >
            Send Message
          </Button>,
        ]}
      >
      <Spin spinning={loadingUsers}>
        <Form form={form} layout="vertical">
          {/* Recipient Selection Type */}
          <Form.Item label="Send To">
            <Select
              value={recipientSelection}
              onChange={(value) => setRecipientSelection(value)}
              placeholder="Select recipient type"
            >
              <Option value="all">Everyone (All Staff & Admins)</Option>
              <Option value="some">By Role (Select Roles)</Option>
              <Option value="one">Specific People</Option>
            </Select>
          </Form.Item>

          {/* Role Selection (when "some" is selected) */}
          {recipientSelection === 'some' && (
            <Form.Item label="Select Roles">
              <Checkbox.Group
                value={selectedRoles}
                onChange={(values) => setSelectedRoles(values as Role[])}
              >
                <Space direction="vertical">
                  <Checkbox value={Role.STAFF}>Staff</Checkbox>
                  <Checkbox value={Role.ADMIN}>Admin</Checkbox>
                  <Checkbox value={Role.SUPER_ADMIN}>Super Admin</Checkbox>
                </Space>
              </Checkbox.Group>
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {filteredOptions.length} user(s) will receive this message
              </div>
            </Form.Item>
          )}

          {/* Individual User Selection (when "one" is selected) */}
          {recipientSelection === 'one' && (
            <Form.Item
              name="recipients"
              label="Select Recipients"
              rules={[{ required: true, message: 'Please select at least one recipient' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select recipients"
                optionFilterProp="label"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={recipientOptions}
              />
            </Form.Item>
          )}

          {/* Send Mode */}
          <Form.Item label="Delivery Mode">
            <Radio.Group value={sendMode} onChange={(e) => setSendMode(e.target.value)}>
              <Space direction="vertical">
                <Radio value="individual">
                  <strong>Individual Emails</strong>
                  <div style={{ fontSize: 12, color: '#666', marginLeft: 24 }}>
                    Send a separate email to each recipient (they won't see other recipients)
                  </div>
                </Radio>
                <Radio value="group">
                  <strong>Group Email</strong>
                  <div style={{ fontSize: 12, color: '#666', marginLeft: 24 }}>
                    Send one email with all recipients visible to each other
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {/* Subject */}
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter a subject' }]}
          >
            <Input placeholder="Message subject" />
          </Form.Item>

          {/* Body */}
          <Form.Item
            name="body"
            label="Message"
            rules={[{ required: true, message: 'Please enter a message' }]}
          >
            <TextArea
              rows={8}
              placeholder="Enter your message here..."
              showCount
              maxLength={2000}
            />
          </Form.Item>

          {/* Info */}
          <div style={{ padding: 12, background: '#f0f5ff', borderRadius: 4, marginTop: 16 }}>
            <div style={{ fontSize: 12, color: '#1890ff' }}>
              <strong>Note:</strong> This message will be sent via email using the same Google Apps Script API
              as automated notifications. Recipients will receive this as an email to their registered address.
            </div>
          </div>
        </Form>
      </Spin>
      </Modal>
    </>
  );
};
